import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { SectionCard } from "@/components/common/SectionCard";
import { AdsAssistantAggregationSection } from "@/components/ads-assistant/AdsAssistantAggregationSection";
import { AdsHelpHint } from "@/components/ads-assistant/AdsAssistantCollapsible";
import { AdsAssistantFeedImportSection } from "@/components/ads-assistant/AdsAssistantFeedImportSection";
import { AdsAssistantPanelAreaTabs } from "@/components/ads-assistant/AdsAssistantPanelAreaTabs";
import { AdsAssistantOrgSettingsSection } from "@/components/ads-assistant/AdsAssistantOrgSettingsSection";
import type { AdsAssistantPanelIntent, WorkspaceArea } from "@/components/ads-assistant/adsAssistantPanelUtils";

/**
 * Ads Assistant — manual and API-backed ad ingestion, library, and coaching surfaces.
 */
export function AdsAssistantPanel(props: {
  forcedIntent?: AdsAssistantPanelIntent;
  onForcedIntentApplied?: () => void;
}) {
  const { forcedIntent = null, onForcedIntentApplied } = props;
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();

  const [workspaceArea, setWorkspaceArea] = useState<WorkspaceArea>("ingestion");

  const canConfigureFeeds = auth.hasRole(
    "trainer",
    "marketing",
    "admin",
    "owner",
    "dev",
    "platform_admin",
    "platform_dev",
    "platform_trainer",
    "ads_guidance_editor",
  );

  useEffect(() => {
    if (!canConfigureFeeds && (workspaceArea === "org_settings" || workspaceArea === "feed_import")) {
      setWorkspaceArea("ingestion");
    }
  }, [canConfigureFeeds, workspaceArea]);

  useEffect(() => {
    if (!forcedIntent) return;
    const area = forcedIntent.area;
    // Library was re-homed to the Manager-bucket `adsGuidance` mode
    // on 2026-06-15 (see docs/design/admin_split_and_entitlements.md
    // §7.6). Any callers that still pass a `library` intent are
    // legacy navigation paths; redirect to ingestion and consume the
    // intent so the caller stops resending it. Once all callers are
    // migrated, the type can drop the `library` variant entirely.
    if (area === "library") {
      setWorkspaceArea("ingestion");
      onForcedIntentApplied?.();
      return;
    }
    if ((area === "org_settings" || area === "feed_import") && !canConfigureFeeds) {
      setWorkspaceArea("ingestion");
      onForcedIntentApplied?.();
      return;
    }
    setWorkspaceArea(area);
    onForcedIntentApplied?.();
  }, [forcedIntent, canConfigureFeeds, onForcedIntentApplied]);

  const aggregationCommon = {
    scopedOrgId,
    embedded: true as const,
  };

  return (
    <div className="w-full text-left space-y-5">
      <AdsAssistantPanelAreaTabs
        canConfigureFeeds={canConfigureFeeds}
        workspaceArea={workspaceArea}
        setWorkspaceArea={setWorkspaceArea}
      />

      {workspaceArea === "org_settings" && canConfigureFeeds ? (
        <AdsAssistantOrgSettingsSection scopedOrgId={scopedOrgId} />
      ) : null}

      {workspaceArea === "ingestion" ? (
        <AdsAssistantAggregationSection {...aggregationCommon} variant="full" />
      ) : null}

      {workspaceArea === "suggestion" ? (
        <AdsAssistantAggregationSection {...aggregationCommon} variant="browse" />
      ) : null}

      {workspaceArea === "feed_import" && canConfigureFeeds ? (
        <SectionCard
          title="Feed import"
          description="For trainers and operators: fetch Facebook Page posts with a Page token, or paste Graph / Google Business local-post JSON. Merges into the same index as manual entries; optionally tag a location."
          rightSlot={(
            <AdsHelpHint label="Meta / Google \u2014 one org token like CRM?">
              There is <strong>no single drop-in &ldquo;business token&rdquo;</strong> that works like one CRM connection for every
              location. <strong>Meta:</strong> Reading Page posts uses a <strong>Page id</strong> with a{" "}
              <strong>Page access token</strong>. <strong>Google:</strong> Business Profile is per account/location; org OAuth
              would need a Cloud client and secure token storage. This screen starts with <strong>paste</strong> and{" "}
              <strong>direct Meta fetch</strong>.
            </AdsHelpHint>
          )}
        >
          <AdsAssistantFeedImportSection scopedOrgId={scopedOrgId} embedded />
        </SectionCard>
      ) : null}
    </div>
  );
}
